'use client'
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useTransition,
  useLayoutEffect,
  useRef,
  memo,
  Suspense,
  use,
} from 'react'
import { type Params } from './actions'
import type {
  documentTypeFilters,
  sortOptionMappings,
} from '@shared/lib/search'
import type { DocumentSearchResponse } from '@shared/lib/search.server'
import { createStore, useStore, type StoreApi } from 'zustand'
import type { ParserLink } from '@shared/lib/parser/parser'
import { useSearchParams } from 'next/navigation'
import useFirstRender from '@shared/hooks/useFirstRender'
import { nonNullable } from '@shared/lib/utils'
import { sendSlackMessageFrontendError } from '@shared/lib/sendSlackMessage'
import { ensureError } from '@shared/lib/errors'

export type IRichResult = ParserLink & { description?: string } & Record<
    string,
    any
  >
interface DateRange {
  from?: string
  to?: string
}
export interface ISearchFilters {
  documentType?:
    | keyof typeof documentTypeFilters
    | Array<keyof typeof documentTypeFilters>
  institution?: string | string[]
  lawType?: string | string[]
  legalCategories?: string | string[]
  inEffectDate?: DateRange
  outOfEffectDate?: DateRange
  publishedDate?: DateRange
  decisionDate?: DateRange
  jurisdiction?: string | string[]
}

type ISearchResults = DocumentSearchResponse & {
  richResult?: IRichResult
}

export interface SearchContextStore {
  query: string
  results?: ISearchResults | Promise<ISearchResults>
  error?: Error
  isPending?: boolean
  filter?: ISearchFilters | null
  sortBy?: Array<keyof typeof sortOptionMappings>
  page?: number

  pending?: {
    query?: SearchContextStore['query']
    filter?: SearchContextStore['filter']
    sortBy?: SearchContextStore['sortBy']
    page?: SearchContextStore['page']
  }
  setSortBy: (
    sortBy: Array<keyof typeof sortOptionMappings> | undefined,
  ) => void
  setQuery: (query: string) => void
  setFilter: (filter: ISearchFilters | null) => void
  setPage: (page: number) => void
}

interface SearchContextValue {
  store: StoreApi<SearchContextStore>
  params: Params
  id?: string
}

const SearchContext = createContext<SearchContextValue | null>(null)

export interface SearchProviderProps {
  slackMsgEnvironmentPrefix: string
  id?: string
  children: React.ReactNode
  /** Controlled query input. Overrides the provided `params.q` */
  query?: string
  /** Controlled sortBy input. Overrides the provided `params.sortBy` */
  sortBy?: Array<keyof typeof sortOptionMappings>
  page?: number
  results?: SearchContextStore['results']
  params?: Params
  filter?: ISearchFilters
  indexName?: 'documents' | 'query_suggestions' | 'citations'
  richSearch?: boolean
  url?: boolean
  mode?: 'auto' | 'text'
  minCharacters?: number
}

export default function SearchProvider({
  slackMsgEnvironmentPrefix,
  children,
  params: _params = {},
  minCharacters = 1,
  filter = {},
  indexName,
  id,
  richSearch,
  results,
  url = false,
  query,
  sortBy = ['decisionDate'],
  mode,
}: SearchProviderProps) {
  const [store] = useState(() => {
    return createStore<SearchContextStore>()((set) => {
      return {
        query: _params.q ?? '',
        results,
        sortBy,
        setQuery(query) {
          set({ query })
        },
        setFilter(filter) {
          set({ filter: filter === null ? {} : filter })
        },
        setSortBy(sortBy) {
          set({ sortBy })
        },
        setPage(page) {
          set({ page })
        },
      }
    })
  })

  const ref = useRef(_params)
  const paramsChanged = JSON.stringify(_params) !== JSON.stringify(ref.current)
  ref.current = paramsChanged ? _params : ref.current

  const value = {
    store,
    filter,
    results,
    params: ref.current,
    id,
    richSearch,
    url,
    query,
    mode,
    minCharacters,
    slackMsgEnvironmentPrefix,
  }

  return (
    <SearchContext.Provider value={value}>
      {/*
       * Keep data fetching logic in separate child component as to not trigger re-renders for all children.
       * The `useEffect()` call stays in the TypesenseRequest component.
       */}
      <Suspense>
        <TypesenseRequests {...value} indexName={indexName} />
      </Suspense>
      {children}
    </SearchContext.Provider>
  )
}

/**
 * Handles requests to the typesense API and updates the search provider store.
 */
const TypesenseRequests = memo(function TypesenseRequest({
  store,
  params,
  minCharacters = 1,
  filter: defaultFilter,
  indexName,
  id = 'unknown',
  richSearch,
  url,
  query: controlledQuery,
  mode,
  slackMsgEnvironmentPrefix,
}: Omit<
  SearchProviderProps & {
    store: StoreApi<SearchContextStore>
    filter: ISearchFilters
  },
  'children'
>) {
  const queryFromStore = useStore(store, (state) => state.query)
  const query = controlledQuery ?? queryFromStore ?? params?.q ?? ''
  const filter = useStore(store, (state) => state.filter)
  const sortBy = useStore(store, (state) => state.sortBy)
  const page = useStore(store, (state) => state.page)

  const [isPending, startTransition] = useTransition()
  const [results, setResults] = useState<SearchContextStore['results']>(() => {
    return store.getState().results
  })
  const [error, setError] = useState<Error | undefined>()

  const isFirstRender = useFirstRender()

  const [debouncedParams, setDebouncedParams] = useState<
    Record<string, unknown>
  >({})
  useEffect(() => {
    if (isFirstRender) {
      console.debug(`[ID=${id}] skipping because of first render`)
      return
    }
    console.log(`[ID=${id}][live] params update`, {
      defaultFilter,
      filter,
      sortBy,
      page,
      query,
      params,
    })
    const timeout = setTimeout(() => {
      console.log(`[ID=${id}][debounced] params update`)
      setDebouncedParams({
        defaultFilter,
        filter,
        sortBy,
        page,
        query,
        params,
      })
    }, 300)
    return () => {
      clearTimeout(timeout)
    }
  }, [defaultFilter, filter, sortBy, page, query, params])

  useEffect(() => {
    if (controlledQuery) store.getState().setQuery(controlledQuery)
  }, [controlledQuery, store])

  useEffect(() => {
    if (isFirstRender && results) return // If the request was server rendered, don't re-fetch on initial load
    if (query.length <= minCharacters) return // Don't fetch if query is too short

    const controller = new AbortController()
    const signal = controller.signal

    startTransition(async () => {
      try {
        const combinedFilters = {
          ...defaultFilter,
          ...removeUndefined({ ...filter }),
        }
        const _filter = Object.entries(combinedFilters)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => {
            if (Array.isArray(value)) {
              return `(${value.map((item) => `${key}:"${item as string}"`).join(' || ')})`
            }
            if (typeof value === 'object') {
              return [
                value.from
                  ? `${key}:>${new Date(value.from).getTime()}`
                  : undefined,
                value.to
                  ? `${key}:<${new Date(value.to).getTime()}`
                  : undefined,
              ].filter(nonNullable)
            }
            return `${key}:${value}`
          })
          .flat()

        const paramsFilterBy = (
          Array.isArray(params?.filter_by)
            ? params?.filter_by
            : [params?.filter_by]
        )
          ?.flat()
          .filter(nonNullable)

        const response = await fetch('/api/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: id + '[client]',
            indexName,
            page,
            richSearch,
            mode,
            ...params,
            q: query,
            sort_by:
              sortBy && sortBy.length > 0
                ? `_text_match:desc,${sortBy.map((sort) => `${sort}:desc`).join(',')}`
                : undefined,
            filter_by: [..._filter, ...paramsFilterBy],
          }),
          signal,
        })

        // Vercel firewall challenge/rate limit (bots that can't solve it); /api/search never returns 429 itself
        if (
          response.status === 429 ||
          response.headers.has('x-vercel-mitigated')
        ) {
          return
        }

        const rawBody = await response.text()
        // Plain errors instead of tiny-invariant: invariant() strips messages
        // in production, turning every alarm into an unactionable "Invariant failed"
        if (!response.ok) {
          throw new Error(
            `Search request failed: HTTP ${response.status}${
              rawBody ? `: ${rawBody.slice(0, 300)}` : ''
            }`,
          )
        }
        if (!rawBody) throw new Error('Empty search response body')

        try {
          setResults(JSON.parse(rawBody))
        } catch {
          throw new Error('Search response is invalid JSON')
        }
      } catch (caught) {
        const error = ensureError(caught)
        const msg = error.message
        if (
          // Request was cancelled on purpose (unmount, params changed, navigation)
          signal.aborted ||
          error.name === 'AbortError' ||
          // Network-level failures (dropped connection, closed tab, offline):
          // nothing actionable for us, don't alarm
          msg === 'Failed to fetch' ||
          msg === 'Load failed' ||
          msg === 'NetworkError when attempting to fetch resource.' ||
          msg === 'The network connection was lost.' ||
          msg === 'cancelled' ||
          (typeof navigator !== 'undefined' && !navigator.onLine)
        ) {
          return
        }
        console.info(
          `[ID=${id}] Could not fetch search results`,
          error,
          debouncedParams,
        )
        sendSlackMessageFrontendError(
          `${slackMsgEnvironmentPrefix} Error making search query [ID=${id}]: ${msg || error}\n\nParams: ${JSON.stringify(debouncedParams, null, 2)}\n\nQuery: ${query}`,
        ).catch((error) => {
          console.error('Failed to send message:', error)
        })
        setError(error)
      }
    })

    return () => {
      if (!signal.aborted) controller.abort()
    }
  }, [debouncedParams])

  useLayoutEffect(() => {
    // isPending check to make sure we only set results for the last request
    store.setState(({ results: _results }) => ({
      isPending,
      results: isPending ? _results : results,
      error,
    }))
  }, [isPending, results, error, store])

  const searchParams = useSearchParams()
  const q = searchParams?.get('q')
  useEffect(
    function syncUrlToState() {
      if (url) store.setState({ query: q ?? '' })
    },
    [url, q, store],
  )

  return null
})

export function useSearch<T = SearchContextStore>(
  selector?: (state: SearchContextStore) => T,
) {
  const { store } = useContext(SearchContext) ?? {}
  if (!store) throw new Error('useSearch must be used within a SearchProvider')

  // @ts-expect-error Don't know why it doesn't want the selector type...
  return useStore(store, selector)
}

function removeUndefined<T extends Record<string, unknown>>(
  data: T,
): Partial<T> {
  const cleaned = Object.entries(data)
    .filter(([, value]) => value !== undefined)
    .reduce<Partial<T>>((obj, [key, value]) => {
      obj[key as keyof T] = value as T[keyof T]
      return obj
    }, {})
  return cleaned
}

export function useSearchResults(): ISearchResults {
  const results = useSearch((state) => state.results)
  /**
   * `results` can either be a promise or the actual results object.
   * In the initial response, the promise is streamed to the client and a fallback is shown via Suspense.
   * By streaming the initial search response, the rest of the page can already be shown the the user.
   */

  // If results is a Promise<ISearchResults>, use the 'use' hook to unwrap it.
  // Checking `.then()` is the only way to reliably detect Promises.
  if (typeof (results as PromiseLike<ISearchResults>)?.then === 'function') {
    return use(results as Promise<ISearchResults>)
  }

  return results as ISearchResults
}
